"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";

type Fill = { verify: number; trace: number; finance: number };
export type Pillar = "verify" | "trace" | "finance";

/**
 * PillarRing renders the three concentric arcs at the top of every audience-
 * facing screen. Per design principle 1: the pillars are always visible.
 *
 *   verify  → outermost ring, --state-verified
 *   trace   → middle ring,    --state-traced
 *   finance → inner ring,     --state-financed
 *
 * Fill values are 0..1; the arc length per pillar is fillPct * 360°.
 *
 * `pulse` triggers a transient 600 ms glow on the named pillar — used by
 * HUD when an anchor event lands (document attestation, LC issuance,
 * insurance cert anchor, compliance snapshot). Pass a fresh `pulseKey`
 * each time you want a re-pulse (the key resets the animation so
 * back-to-back anchors don't merge into one pulse).
 */
export function PillarRing({
  fill,
  size = 84,
  pulse,
  pulseKey,
  className,
}: {
  fill: Fill;
  size?: number;
  pulse?: Pillar | null;
  pulseKey?: number;
  className?: string;
}) {
  const stroke = 4;
  const labelSize = 9;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;

  const radii = [r, r - 9, r - 18];
  const colours = ["var(--state-verified)", "var(--state-traced)", "var(--state-financed)"];
  const fills = [fill.verify, fill.trace, fill.finance];
  const pillarIndex: Record<Pillar, number> = { verify: 0, trace: 1, finance: 2 };

  // Transient pulse — clears itself after 600 ms so the animation doesn't
  // accumulate state. New events trigger a re-pulse via pulseKey changes.
  const [livePulse, setLivePulse] = useState<Pillar | null>(null);
  useEffect(() => {
    if (!pulse) return;
    setLivePulse(pulse);
    const t = setTimeout(() => setLivePulse(null), 700);
    return () => clearTimeout(t);
  }, [pulse, pulseKey]);

  return (
    <svg viewBox={`0 0 ${size} ${size}`} width={size} height={size} className={clsx("shrink-0", className)} aria-label="Pillar progress">
      {radii.map((rr, i) => (
        <circle
          key={`base-${i}`}
          cx={cx}
          cy={cy}
          r={rr}
          fill="none"
          stroke="rgba(255,255,255,0.06)"
          strokeWidth={stroke}
        />
      ))}
      {radii.map((rr, i) => {
        const circumference = 2 * Math.PI * rr;
        const dash = circumference * Math.min(Math.max(fills[i], 0), 1);
        const isPulsing = livePulse !== null && pillarIndex[livePulse] === i;
        return (
          <circle
            key={`fill-${i}`}
            cx={cx}
            cy={cy}
            r={rr}
            fill="none"
            stroke={colours[i]}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={`${dash} ${circumference}`}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{
              // Pulse layers a stronger glow + slight stroke widening on
              // the anchored pillar; otherwise the base drop-shadow stays.
              filter: isPulsing
                ? `drop-shadow(0 0 14px ${colours[i]}) drop-shadow(0 0 6px ${colours[i]})`
                : fills[i] > 0.05
                  ? `drop-shadow(0 0 6px ${colours[i]})`
                  : undefined,
              transition: isPulsing
                ? "stroke-dasharray 600ms cubic-bezier(0.4, 0, 0.2, 1), filter 200ms ease-out"
                : "stroke-dasharray 600ms cubic-bezier(0.4, 0, 0.2, 1), filter 500ms ease-out",
            }}
          />
        );
      })}
      <text x={cx} y={cy + 3} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize={labelSize} fontFamily="var(--font-mono)" letterSpacing="0.18em">
        V·T·F
      </text>
    </svg>
  );
}
